<template>
  <v-dialog :value="value" @input="closeDialog" fullscreen dark persistent transition="fade-transition">
    <v-card style="background-color: rgba(0, 0, 0, 0.9); backdrop-filter: blur(10px)" flat class="d-flex flex-column fill-height pa-4">
      <v-toolbar dark color="transparent" flat>
        <span v-if="fileCount > 1" class="attachment-position">Datei {{ fileIndex }} von {{ fileCount }}</span>
        <v-spacer />
        <v-btn icon large :disabled="isLoading" aria-label="Anhänge schliessen" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <div v-if="isLoading" class="px-4 w-100" style="width: 100%">
        <div class="text-center caption mb-1 white--text">Upload: {{ uploadProgress }}%</div>
        <v-progress-linear :value="uploadProgress" color="primary" height="6" rounded striped></v-progress-linear>
      </div>
      <v-card-text class="flex-grow-1 d-flex justify-center align-center pa-0">
        <template v-if="isImageVideoAudio && localPreviewUrl">
          <v-img v-if="fileType.startsWith('image/')" :src="localPreviewUrl" contain class="media-preview-content" />
          <video v-if="fileType.startsWith('video/')" :src="localPreviewUrl" controls class="media-preview-content" />
          <audio v-if="fileType.startsWith('audio/')" :src="localPreviewUrl" controls class="media-preview-content" />
        </template>
        <template v-else>
          <v-sheet color="grey darken-3" rounded="xl" class="pa-8 d-flex flex-column align-center">
            <v-icon size="100" color="grey lighten-1">mdi-file-document-outline</v-icon>
            <span class="headline mt-4">{{ file.name }}</span>
            <span class="caption grey--text">{{ (file.size / 1024).toFixed(0) }} KB</span>
          </v-sheet>
        </template>
      </v-card-text>

      <v-card-actions class="pa-0 flex-column">
        <!-- Schedule Picker (inline, collapsed by default) -->
        <v-expand-transition>
          <div v-if="showSchedulePicker" class="schedule-inline-picker">
            <div class="schedule-inline-header">
              <v-icon small color="primary" class="mr-2">mdi-clock-outline</v-icon>
              <span class="schedule-inline-title">Versand planen</span>
              <v-spacer></v-spacer>
              <v-btn icon x-small @click="showSchedulePicker = false">
                <v-icon small color="grey">mdi-close</v-icon>
              </v-btn>
            </div>
            <div class="schedule-inline-chips">
              <div
                v-for="option in scheduleQuickOptions"
                :key="option.label"
                class="schedule-inline-chip"
                :class="{ 'schedule-inline-chip-active': selectedQuick === option.label }"
                @click="selectQuick(option)">
                <v-icon x-small class="mr-1" :color="selectedQuick === option.label ? 'white' : 'grey'">{{ option.icon }}</v-icon>
                {{ option.label }}
                <span class="schedule-inline-chip-time">{{ option.timePreview }}</span>
              </div>
              <div
                class="schedule-inline-chip"
                :class="{ 'schedule-inline-chip-active': selectedQuick === 'custom' }"
                @click="selectedQuick = 'custom'">
                <v-icon x-small class="mr-1" :color="selectedQuick === 'custom' ? 'white' : 'grey'">mdi-calendar-edit</v-icon>
                Eigene Zeit
              </div>
            </div>
            <v-expand-transition>
              <div v-if="selectedQuick === 'custom'" class="schedule-inline-custom">
                <input type="date" v-model="scheduleDate" :min="minDate" class="schedule-inline-input" />
                <select v-model="scheduleTime" class="schedule-inline-input" style="appearance: auto; cursor: pointer">
                  <option v-for="time in timeOptions" :key="time" :value="time">{{ time }}</option>
                </select>
              </div>
            </v-expand-transition>
            <div v-if="schedulePreview" class="schedule-inline-preview">
              <v-icon x-small color="primary" class="mr-1">mdi-send-clock</v-icon>
              {{ schedulePreview }}
            </div>
            <div v-if="scheduleError" class="schedule-inline-error">{{ scheduleError }}</div>
          </div>
        </v-expand-transition>

        <v-sheet dark class="message-input-area pa-2" elevation="4" color="grey darken-3" style="width: 100%">
          <v-text-field
            v-model="textContent"
            placeholder="Beschriftung hinzufügen..."
            filled
            rounded
            dense
            style="align-items: center !important"
            hide-details
            dark
            autocomplete="off">
            <template v-slot:append-outer>
              <div class="d-flex align-center" style="gap: 6px; margin: auto !important">
                <v-btn
                  fab
                  x-small
                  depressed
                  color="secondary"
                  @click="toggleSchedulePicker"
                  title="Zeitversetzt senden"
                  :outlined="showSchedulePicker">
                  <v-icon small>mdi-clock-outline</v-icon>
                </v-btn>
                <v-btn fab small depressed :color="hasSchedule ? 'orange' : 'primary'" :loading="isLoading" @click="sendFile">
                  <v-icon>{{ hasSchedule ? "mdi-send-clock" : "mdi-send" }}</v-icon>
                </v-btn>
              </div>
            </template>
          </v-text-field>
        </v-sheet>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// (Wir brauchen Axios und die Gruppen-ID)
import axios from "axios"
import { mapState } from "vuex"

export default {
  name: "SendMediaPreviewDialog",
  props: {
    value: Boolean, // v-model
    groupId: { type: String, default: null },
    fileIndex: { type: Number, default: 1 },
    fileCount: { type: Number, default: 1 },
    file: {
      // Die rohe Datei vom <input>
      type: File,
      default: null,
    },
  },
  data() {
    return {
      textContent: "",
      localPreviewUrl: null,
      fileType: "",
      isImageVideoAudio: false,
      isLoading: false,
      uploadProgress: 0,
      // Schedule
      showSchedulePicker: false,
      selectedQuick: null,
      scheduleDate: new Date().toISOString().substr(0, 10),
      scheduleTime: null,
      scheduleError: null,
    }
  },
  computed: {
    ...mapState("chatStore", ["activeGroupId"]),
    minDate() {
      return new Date().toISOString().substr(0, 10)
    },
    timeOptions() {
      const times = []
      for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
          times.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`)
        }
      }
      return times
    },
    scheduleQuickOptions() {
      const now = new Date()
      const inOneHour = new Date(now.getTime() + 60 * 60 * 1000)
      const m1 = inOneHour.getMinutes()
      inOneHour.setMinutes(Math.ceil(m1 / 15) * 15, 0, 0)
      const todayEvening = new Date(now)
      todayEvening.setHours(18, 0, 0, 0)
      const tomorrowMorning = new Date(now)
      tomorrowMorning.setDate(tomorrowMorning.getDate() + 1)
      tomorrowMorning.setHours(8, 0, 0, 0)
      const options = []
      options.push({
        label: "In 1 Stunde",
        icon: "mdi-clock-fast",
        date: inOneHour,
        timePreview: inOneHour.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }),
      })
      if (todayEvening > now) options.push({ label: "Heute Abend", icon: "mdi-weather-night", date: todayEvening, timePreview: "18:00" })
      options.push({
        label: "Morgen Früh",
        icon: "mdi-weather-sunny",
        date: tomorrowMorning,
        timePreview: tomorrowMorning.toLocaleDateString("de-CH", { weekday: "short" }) + " 08:00",
      })
      return options
    },
    computedScheduleDate() {
      if (this.selectedQuick && this.selectedQuick !== "custom") {
        const opt = this.scheduleQuickOptions.find((o) => o.label === this.selectedQuick)
        return opt ? opt.date : null
      }
      if (this.selectedQuick === "custom" && this.scheduleDate && this.scheduleTime) {
        return new Date(`${this.scheduleDate}T${this.scheduleTime}:00`)
      }
      return null
    },
    schedulePreview() {
      const dt = this.computedScheduleDate
      if (!dt) return null
      const now = new Date()
      const isToday = dt.toDateString() === now.toDateString()
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const isTomorrow = dt.toDateString() === tomorrow.toDateString()
      const day = isToday ? "Heute" : isTomorrow ? "Morgen" : dt.toLocaleDateString("de-CH", { weekday: "short", day: "numeric", month: "short" })
      return `Versand: ${day}, ${dt.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })} Uhr`
    },
    hasSchedule() {
      return this.showSchedulePicker && this.computedScheduleDate && this.computedScheduleDate > new Date()
    },
  },
  watch: {
    file: {
      handler(newFile) {
        if (newFile) {
          if (this.localPreviewUrl) URL.revokeObjectURL(this.localPreviewUrl)
          this.localPreviewUrl = URL.createObjectURL(newFile)
          this.fileType = newFile.type
          this.isImageVideoAudio = newFile.type.startsWith("image/") || newFile.type.startsWith("video/") || newFile.type.startsWith("audio/")
        }
      },
      immediate: true,
    },
  },
  methods: {
    closeDialog() {
      if (this.isLoading) return
      this.showSchedulePicker = false
      this.selectedQuick = null
      this.$emit("input", false)
    },
    toggleSchedulePicker() {
      this.showSchedulePicker = !this.showSchedulePicker
      if (this.showSchedulePicker) {
        this.scheduleError = null
        this.selectedQuick = null
        const now = new Date()
        const minutes = now.getMinutes()
        const remainder = 15 - (minutes % 15)
        now.setMinutes(minutes + remainder)
        this.scheduleDate = now.toISOString().substr(0, 10)
        this.scheduleTime = now.toTimeString().substr(0, 5)
      }
    },
    selectQuick(option) {
      this.selectedQuick = option.label
      this.scheduleError = null
    },
    async sendFile() {
      const groupId = this.groupId || this.activeGroupId
      if (!this.file || !groupId || this.isLoading) return

      const MAX_FILE_SIZE_MB = 200
      const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
      if (this.file.size > MAX_FILE_SIZE_BYTES) {
        if (this.$toast) this.$toast.error(`Datei zu gro\u00df. Maximal ${MAX_FILE_SIZE_MB} MB.`)
        else alert(`Datei zu gro\u00df. Maximal ${MAX_FILE_SIZE_MB} MB.`)
        return
      }

      // Validate schedule if picker is open
      if (this.showSchedulePicker) {
        const scDateTime = this.computedScheduleDate
        if (!scDateTime) {
          this.scheduleError = "Bitte eine Zeit ausw\u00e4hlen."
          return
        }
        if (scDateTime <= new Date()) {
          this.scheduleError = "Die gew\u00e4hlte Zeit muss in der Zukunft liegen."
          return
        }
      }

      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl)
        this.localPreviewUrl = null
      }
      this.isImageVideoAudio = false

      this.isLoading = true
      this.uploadProgress = 0

      const formData = new FormData()
      formData.append("file", this.file)
      formData.append("textContent", this.textContent)

      // Append scheduling fields if a schedule is set
      if (this.hasSchedule) {
        formData.append("scheduledFor", this.computedScheduleDate.toISOString())
        formData.append("isSent", "false")
      }

      try {
        const token = localStorage.getItem("token")
        await axios.post(`/api/chat/groups/${groupId}/media`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            }
          },
        })

        this.isLoading = false
        this.$emit("sent")
        this.closeDialog()
      } catch (err) {
        console.error("Fehler beim Datei-Upload:", err)
        let errorMsg = "Upload fehlgeschlagen."
        if (err.response && err.response.status === 413) {
          errorMsg = "Die Datei ist zu gro\u00df f\u00fcr den Server."
        }
        if (this.$toast) this.$toast.error(errorMsg)
        else alert(errorMsg)
        this.isLoading = false
      }
    },
  },
  beforeDestroy() {
    // WICHTIG: Letzte lokale URL freigeben, wenn Komponente zerstört wird
    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl)
    }
  },
}
</script>

<style lang="scss" scoped>
.attachment-position {
  font-size: 0.875rem;
  line-height: 1.5;
}

.media-preview-content {
  max-width: 90vw;
  max-height: 70vh;
  border-radius: 8px;
}
.message-input-area {
  border-radius: 28px !important;
}

/* Schedule Inline Picker */
.schedule-inline-picker {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px 16px 0 0;
}

.schedule-inline-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.schedule-inline-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.schedule-inline-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.schedule-inline-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
}

.schedule-inline-chip-active {
  background: linear-gradient(135deg, var(--v-primary-base, #4caf50), #2e7d32) !important;
  border-color: transparent !important;
  color: #fff !important;
}

.schedule-inline-chip-time {
  margin-left: 6px;
  opacity: 0.5;
  font-size: 0.72rem;
}

.schedule-inline-custom {
  display: flex;
  gap: 8px;
  padding: 8px 0;
}

.schedule-inline-input {
  flex: 1;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 6px 10px;
  color: #fff;
  font-size: 0.85rem;
  font-family: inherit;
  outline: none;
  color-scheme: dark;

  /* Dropdown Optionen im Dark Mode */
  option {
    background-color: #1e1e24;
    color: #ffffff;
  }
}

.schedule-inline-preview {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.78rem;
  margin-top: 4px;
}

.schedule-inline-error {
  color: #ef5350;
  font-size: 0.75rem;
  padding: 4px 0;
}
</style>
